import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'
import { initializeFirestore } from 'firebase/firestore'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { teamsByUid } from './firestore.js'
import { updateUserId } from './sentry'
import commitUTMParams from './utils/commitUTMParams.js'
import * as GtagService from './utils/GtagService.js'
import * as PaddleService from './utils/PaddleService.js'
import { getFirebaseConfig } from './utils/getFirebaseConfig.js'

// Initialize Firebase
const firebaseConfig = getFirebaseConfig()
export const app = initializeApp(firebaseConfig)
// Initialize Analytics only if supported
export const analytics = getAnalytics(app)
export const db = initializeFirestore(app, {
  // A WebKit bug in Safari 26.4+ (and every iOS browser) intermittently delays
  // streamed listener updates by ~30s. Switching to long polling avoids the issue.
  // Reportedly fixed in WebKit, we should remove it once the fix has reached most users.
  // SDK issue: https://github.com/firebase/firebase-js-sdk/issues/9789
  // WebKit issue: https://bugs.webkit.org/show_bug.cgi?id=322545
  experimentalForceLongPolling: isWebKit(),
})
export const auth = getAuth(app)
let unsubscribe = null

/**
 * Safari and every iOS browser, which all run on WebKit.
 *
 * @remarks
 *   Same check as `isSafariOrWebkit` in `@firebase/util`.
 *   All Chromium-based browsers are excluded by `Chrome`.
 *   Firefox on desktop and mobile also excluded.
 *
 * @returns {boolean}
 */
function isWebKit() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return (ua.includes('Safari') || ua.includes('WebKit')) && !ua.includes('Chrome')
}

// Create an awaitable authStateInit
let authStateResolve
const authStateInit = new Promise((r) => (authStateResolve = r))

/**
 * Initialize the auth state listener.
 * Makes sure the onAuthStateChanged listener is only attached once per pageload.
 *
 * Note: Initiate Gtag when we have a user, this way we know that they accepted the privacy policy.
 * Same for Paddle to activate Paddle Retain and Engagement.
 *
 * @returns {void}
 *
 * @idempotent
 */
function initializeAuthStateListener() {
  if (unsubscribe) return
  unsubscribe = onAuthStateChanged(auth, (user) => {
    updateUserId(user?.uid)
    if (user) {
      void GtagService.init(firebaseConfig.measurementId, firebaseConfig.adsId)
      // Retain warm-up only, and ad blockers break it routinely. Checkout
      // inits again on its own, so a failure here is not worth reporting.
      PaddleService.init().catch((err) => console.warn('[Paddle] init failed:', err))
    }
    if (user?.email) commitUTMParams()
    authStateResolve()
  })
}

/**
 * Get the current user after ensuring auth state is initialized.
 */
export async function getUser() {
  await authStateInit
  return auth.currentUser
}

/**
 * Get a user's teams.
 * Return an empty array if there's no user.
 *
 * @returns {Promise<import('~shared/types').Team[]>}
 */
export async function getUserTeams() {
  const user = await getUser()
  if (!user) return []
  const { data: teams } = await teamsByUid(user.uid)
  return teams
}

// Initialize the auth state listener immediately
initializeAuthStateListener()
